// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAccessibleSubmit from "@insite/client-framework/Common/Hooks/useAccessibleSubmit";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import QtyField, { QtyFieldStyles } from "@turnerBlueprint/App/Components/Qty/QtyField";
import IconWarning from "@turnerBlueprint/XDL/Icons/IconWarning";
import { mergeWithCss } from "@turnerBlueprint/XDL/Utilities";
import React, { FC } from "react";
import { connect } from "react-redux";

interface OwnProps {
    cart: Cart;
    /**
     * If true, the qtyOrdered will display in a text field for editing.
     * If false, the qtyOrdered will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onQtyOrderedChange?: (qtyOrdered: number) => void;
    extendedStyles?: CartLineQuantityStyles;
    label?: string;
    // XNG-Changes
    showLabel?: boolean;
    showActionButton?: boolean;
    disabled?: boolean;
    unEligibleQty?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    isLoading: state.data.carts.isLoading,
    cartLoadedTime: state.data.carts.cartLoadedTime,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasCartLineContext;

export interface CartLineQuantityStyles {
    editableQuantityTextField?: QtyFieldStyles;
    readOnlyQuantityText?: QtyFieldStyles;
    // XNG-Changes
    unEligibleQtyIcon?: IconPresentationProps;
}

export const cartLineQuantityStyles: CartLineQuantityStyles = {
    // editableQuantityTextField: {
    //     labelProps: {
    //         variant: "legend",
    //         css: css`
    //             font-size: 10px;
    //             text-transform: uppercase;
    //             font-weight: 600;
    //         `,
    //     },
    // },
};

const CartLineQuantity: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    label,
    isLoading,
    cartLoadedTime,
    onQtyOrderedChange = () => {},
    extendedStyles,
    // XNG-Change
    showLabel = false,
    showActionButton,
    disabled = false,
    unEligibleQty = false,
}) => {
    const localOnQtyOrderedChange = (value: string) => {
        const inputQty = parseFloat(value);
        if (Number.isNaN(inputQty)) {
            return;
        }

        onQtyOrderedChange(inputQty);
    };

    const { value, changeHandler, keyDownHandler, blurHandler } = useAccessibleSubmit(
        cartLine.qtyOrdered!.toString(),
        localOnQtyOrderedChange,
        cartLoadedTime,
    );

    const [styles] = React.useState(() => mergeWithCss(cartLineQuantityStyles, extendedStyles));

    if (editable && !isOutOfStock(cartLine)) {
        return (
            <>
                <QtyField
                    showLabel={showLabel}
                    value={value}
                    disabled={
                        !cart.canModifyOrder ||
                        cartLine.isPromotionItem ||
                        cart.type === "Job" ||
                        isLoading[cart.id] ||
                        // XNG-Changes
                        disabled
                    }
                    onChange={changeHandler}
                    onKeyDown={keyDownHandler}
                    onBlur={blurHandler}
                    onClick={event => event.currentTarget.focus()}
                    data-test-selector="cartline_qty"
                    // Xng-Changes
                    min={1}
                    extendedStyles={styles.editableQuantityTextField}
                    showActionButton={showActionButton}
                    // showNoPriceAlert={cartLine.quoteRequired}
                />
                {/* XNG-Changes */}
                {unEligibleQty && (
                    <Icon src={IconWarning} size={20} style={{ marginLeft: "8px", position: "relative", top: "4px" }} />
                )}
            </>
        );
    }

    // XNG-changes: Using QtyField Component with editable={false} to display the number as text
    return <QtyField editable={false} value={value} extendedStyles={styles.readOnlyQuantityText} />;
};

export default connect(mapStateToProps)(withCartLine(CartLineQuantity));
